import { Box, Button, MenuItem, Select } from "@material-ui/core";
import useSWR from "swr";
import { fetcher } from "utils/apiRequestWithErrorCode";
import { FilterPolicy } from "@dashboard-v3/api";
import { useRuleContext } from "../context/ruleContext";
import SwitchField from "components/Forms/SwitchField";
import { useTranslation } from "react-i18next";
import { getContentFilter } from "pages/Rules/Form/context/ruleSelectors";
import { useFilterCopy } from "./Action/copy";

interface Props {
  filterType: "ACTION" | "TARGET";
  readOnly?: boolean;
}

export default function FilterPolicyToggle(props: Props) {
  const { filterType, readOnly = false } = props;
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { isAttachmentVariant } = useFilterCopy();
  const { data: filterOptions, isLoading } = useSWR<FilterPolicy[]>(
    "/filter-policies",
    fetcher
  );

  const noFilterOptions = filterOptions?.length === 0;
  const selection =
    filterType === "ACTION"
      ? getContentFilter(state.rule)?.filterPolicyId
      : state.rule.filterPolicyId;

  function setFilterPolicy(filterPolicyId: string) {
    dispatch({
      type: "updateFilterPolicy",
      payload: { filterPolicyId, filterType: filterType },
    });
  }

  function onUseFilterPolicy(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    setFilterPolicy(checked ? filterOptions[0].id : "");
  }

  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <SwitchField
          name="filterPolicyEnabled"
          label={t("filterPolicy.toggle")}
          description={t(filterPolicyDesc(filterType, isAttachmentVariant))}
          value={Boolean(selection)}
          onChange={onUseFilterPolicy}
          disabled={isLoading || noFilterOptions || readOnly}
        />
        {noFilterOptions && (
          <Box ml={2}>
            <Button
              color="primary"
              href="/filter-policies/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("filterPolicy.create")}
            </Button>
          </Box>
        )}
      </Box>
      {filterOptions && Boolean(selection) && (
        <Box mb={3} mt={2}>
          <SelectFilterPolicy
            value={selection}
            onChange={setFilterPolicy}
            options={filterOptions}
            disabled={readOnly}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * The action wording is written for attachment processing ("saves the
 * attachment"), which does not describe what the policy does on Temporary Hold
 * or Request Approval: there it gates the rule itself.
 */
function filterPolicyDesc(
  filterType: Props["filterType"],
  isAttachmentVariant: boolean
) {
  if (filterType === "TARGET") return "filterPolicy.targetFilterDesc";
  if (isAttachmentVariant) return "filterPolicy.attachmentFilterDesc";
  return "filterPolicy.actionFilterDesc";
}

function SelectFilterPolicy({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: FilterPolicy["id"] | "";
  onChange: (value: FilterPolicy["id"] | "") => void;
  options: FilterPolicy[];
  disabled?: boolean;
}) {
  return (
    <Select
      variant="outlined"
      value={value}
      disabled={disabled}
      onChange={event => {
        onChange(event.target.value as string);
      }}
    >
      {options.map(p => (
        <MenuItem key={p.id} value={p.id}>
          {p.name}
        </MenuItem>
      ))}
    </Select>
  );
}
